import _Vue, { VNode } from 'vue'
import { layout } from '@/tokens'

// The single choke point for every toast. `$bvToast.toast` is banned outside
// this file (eslint.config.mjs) so that the two shapes, the toaster position
// and the auto-dismiss split live in one place rather than being restated --
// and drifting -- at two dozen call sites.
// Shapes, deviations and the z-index decision: docs/decisions/0002-toast-policy.md
//
// This plugin deliberately does NOT cap the stack. See the ADR: the cap and the
// message coalescing it forces are behaviour, not styling, and are deferred.

/** `'5000ms'` -> `5000`: the generated token module types every value as a string. */
function toNumber(token: string): number {
  return parseInt(token, 10)
}

const AUTO_HIDE_DELAY = toNumber(layout.timingToastDismiss)

let counter = 0

/** Ids exist so the single-line shapes can hide the toast they belong to. */
function nextId(): string {
  counter += 1

  return `blytz-toast-${counter}`
}

interface ToastPayload {
  id: string
  toaster: string
  variant: string
  appendToast: boolean
  noAutoHide: boolean
  title?: string
  autoHideDelay?: number
  noCloseButton?: boolean
}

/**
 * Dismiss control for the single-line shapes, which suppress the native header.
 * A real <button>, not a span with role="button": the header button it stands in
 * for is focusable, and a span without a tab stop cannot be reached by keyboard.
 * It carries Bootstrap's `.close` class so both hosts share one set of styles.
 */
function dismissButton(vm: _Vue, id: string): VNode {
  const h = vm.$createElement

  return h(
    'button',
    {
      class: 'close blytz-toast__dismiss',
      attrs: { type: 'button', 'aria-label': vm.$t('buttons.dismiss') },
      on: { click: () => vm.$bvToast.hide(id) }
    },
    '×'
  )
}

function fire(
  vm: _Vue,
  message: string | VNode[],
  payload: ToastPayload
): void {
  // BootstrapVue accepts a VNode array as the message; its types say string.
  vm.$bvToast.toast(message as string, payload)
}

function basePayload(id: string, variant: string): ToastPayload {
  return {
    id,
    variant,
    toaster: 'b-toaster-bottom-center',
    appendToast: true,
    noAutoHide: false
  }
}

/**
 * Whole-form, network and session failures, and every payment decline.
 * Manual-dismiss only. `title` renders in bold above the message.
 */
function error(vm: _Vue, message: string, title: string): void {
  fire(vm, message, {
    ...basePayload(nextId(), 'danger'),
    title,
    noAutoHide: true
  })
}

function singleLine(vm: _Vue, variant: string, message: string): void {
  const id = nextId()
  const h = vm.$createElement

  fire(
    vm,
    [
      h('span', { class: 'blytz-toast__message' }, message),
      dismissButton(vm, id)
    ],
    {
      ...basePayload(id, variant),
      autoHideDelay: AUTO_HIDE_DELAY,
      noCloseButton: true
    }
  )
}

export interface BlytzToast {
  error(message: string, title: string): void
  success(message: string): void
  info(message: string): void
}

export default {
  install(Vue: typeof _Vue) {
    // A getter, not an assignment: `$bvToast` resolves the toaster relative to
    // the instance it is called on, so each caller needs its own binding.
    Object.defineProperty(Vue.prototype, '$toast', {
      get(this: _Vue): BlytzToast {
        const vm = this

        return {
          error: (message, title) => error(vm, message, title),
          success: message => singleLine(vm, 'success', message),
          info: message => singleLine(vm, 'info', message)
        }
      }
    })
  }
}

/** Test seam: the id counter is module state and would leak between specs. */
export function __resetToastIds(): void {
  counter = 0
}
